import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormFileUpload } from '@ipfabric/ui-components/FormFileUpload/FormFileUpload';
import { FormMultiSelect } from '@ipfabric/ui-components/FormMultiSelect/FormMultiSelect';
import { FormSelect } from '@ipfabric/ui-components/FormSelect/FormSelect';
import { FormTextArea } from '@ipfabric/ui-components/FormTextArea/FormTextArea';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import type { SelectOptionObject } from '@ipfabric/ui-components/Select/Select.types';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { CONTAINER_NAME_RE } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import type { FC } from 'react';
import React, { useEffect, useMemo } from 'react';
import { FormProvider, type SubmitHandler, useWatch } from 'react-hook-form';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import { z } from 'zod';
import type {
  Extension,
  ExtensionType,
  ExtensionUser,
} from '../../model/ExtensionsModel';
import {
  ACCESS_PERMISSIONS,
  type AccessPermissionType,
} from '../accessPermissions';
import { ExtensionsDialogLogs } from './ExtensionsDialogLogs';
import { validateEnvironmentVariables } from './validateExtensionsForm';

export type EditedExtension = {
  id?: string;
  name: string;
  slug: string;
  description: string;
  type: ExtensionType;
  environmentVariables: string;
  file: File | null;
  cpu: string;
  memory: string;
  accessibleByUsers: string[];
  accessPermission: AccessPermissionType;
};

interface ExtensionsDialogProps {
  isReadonly?: boolean;
  initialValues?: Extension;
  platformUsers?: ExtensionUser[];
  logs?: string[];
  logsRefreshing?: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  onRefreshLogs?: () => void;
}

const createSafeDockerName = (name: string): string => {
  return name
    .replace(/[^a-z0-9]/gi, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
};

const DEFAULT_CPU_LIMIT = '1';
const DEFAULT_MEMORY_LIMIT = '512';

const validationsMsg = {
  required: 'Required',
  minLength: (length: number) =>
    `Field must have at least ${length} characters`,
};

const fileTypeOptions: SelectOptionObject<ExtensionType>[] = [
  { label: 'Docker Zip', value: 'docker-zip' },
  { label: 'Docker Image', value: 'docker-image' },
];

const FileTypeError = {
  required: 'Required',
  zipIncorrectFileType:
    'Incorrect file type selected. Only .zip files allowed for "Docker Zip"',
  imageIncorrectFileType:
    'Incorrect file type selected. Only .tar and .tar.gz files allowed for "Docker Image"',
} as const;

export const accessPermissionOptions = Object.keys(ACCESS_PERMISSIONS).map(
  (p) => ACCESS_PERMISSIONS[p as AccessPermissionType],
);

const extensionFormSchema = z
  .object({
    name: z.string().min(2, { message: validationsMsg.minLength(2) }),
    slug: z.string().min(1, { message: validationsMsg.required }),
    description: z.string().min(1, { message: validationsMsg.required }),
    type: z.enum(['docker-zip', 'docker-image']),
    environmentVariables: z.string(),
    file: z.any().nullable(),
    cpu: z.string().min(1, { message: validationsMsg.required }),
    memory: z.string().min(1, { message: validationsMsg.required }),
    accessibleByUsers: z.array(z.string()),
    accessPermission: z.enum(['anyone', 'only-me', 'selected-users']),
  })
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: validation on one is beneficial
  .superRefine((values, ctx) => {
    // import file type validation
    if (values.file) {
      const validTypes =
        values.type === 'docker-zip'
          ? [
              'application/octet-stream',
              'multipart/x-zip',
              'application/zip',
              'application/x-zip-compressed',
              'application/x-zip',
            ]
          : ['application/x-tar', 'application/gzip', 'application/x-gzip'];
      if (!validTypes.includes(values.file.type)) {
        ctx.addIssue({
          path: ['file'],
          code: z.ZodIssueCode.custom,
          message:
            values.type === 'docker-zip'
              ? FileTypeError.zipIncorrectFileType
              : FileTypeError.imageIncorrectFileType,
        });
      }
    } else {
      ctx.addIssue({
        path: ['file'],
        code: z.ZodIssueCode.custom,
        message: FileTypeError.required,
      });
    }
    // Environment variable validation (must be key=value per line)
    if (values.environmentVariables) {
      const environmentError = validateEnvironmentVariables({
        environmentVariables: values.environmentVariables,
        keyValueFormatError:
          'Invalid key-value format. Please ensure each environment variable is in the format "key=value", with each entry on a separate line.',
      });
      if (environmentError) {
        ctx.addIssue({
          path: ['environmentVariables'],
          code: z.ZodIssueCode.custom,
          message: environmentError,
        });
      }
    }
    // name
    if (
      values.name &&
      !CONTAINER_NAME_RE.test(createSafeDockerName(values.name))
    ) {
      ctx.addIssue({
        path: ['name'],
        code: z.ZodIssueCode.custom,
        message: 'Name must contain at least 2 [a-zA-Z0-9] characters',
      });
    }
    // CPU
    if (values.cpu && !Number.isNaN(Number(values.cpu))) {
      const cpu = Number(values.cpu);
      if (cpu < 0.01) {
        ctx.addIssue({
          path: ['cpu'],
          code: z.ZodIssueCode.custom,
          message: 'CPU must be greater than or equal to 0,01 units',
        });
      }
    }
    // memory
    if (values.memory && !Number.isNaN(Number(values.memory))) {
      const memory = Number(values.memory);
      if (memory <= 6) {
        ctx.addIssue({
          path: ['memory'],
          code: z.ZodIssueCode.custom,
          message: 'Memory must be greater than 6 units',
        });
      }
    }
    // accessibleByUsers
    if (
      values.accessPermission === 'selected-users' &&
      (!values.accessibleByUsers || values.accessibleByUsers.length === 0)
    ) {
      ctx.addIssue({
        path: ['accessibleByUsers'],
        code: z.ZodIssueCode.custom,
        message: 'At least one user must be selected',
      });
    }
  });

export const ExtensionsDialog: FC<ExtensionsDialogProps> = React.memo(
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Makes more sense to keep all the logic in one place
  (props) => {
    const { isReadonly = false } = props;
    const ctxProps = useGlobalStateContextSelectors({
      username: prop('user', 'username'),
    });

    const selectableUsers = useMemo(
      () =>
        props.platformUsers?.filter(
          (user) => user.username !== ctxProps.username,
        ),
      [props.platformUsers, ctxProps.username],
    );

    const methods = useCustomForm<EditedExtension>({
      resolver: zodResolver(extensionFormSchema),
      defaultValues: {
        accessibleByUsers: props.initialValues?.accessibleByUsers ?? [],
        accessPermission: props.initialValues?.accessPermission ?? 'anyone',
        description: props.initialValues?.description ?? '',
        slug: props.initialValues?.slug ?? '',
        file: null,
        name: props.initialValues?.name ?? '',
        environmentVariables:
          props.initialValues?.environmentVariables !== undefined
            ? props.initialValues.environmentVariables
                .map(({ key, value }): string => `${key}=${value}`)
                .join('\n')
            : '',
        type: props.initialValues?.type ?? 'docker-zip',
        cpu: props.initialValues?.resources?.cpu ?? DEFAULT_CPU_LIMIT,
        memory: props.initialValues?.resources?.memory ?? DEFAULT_MEMORY_LIMIT,
      },
    });

    const { control, handleSubmit, resetField, formState, setValue } = methods;

    const accessPermission = useWatch({
      control,
      name: 'accessPermission',
    });
    const name = useWatch({
      control,
      name: 'name',
    });

    useEffect(() => {
      setValue('slug', createSafeDockerName(name));
    }, [name, setValue]);

    const api = useApi();
    const notifications = useNotifications();

    const selectableUsersOptions = useMemo(
      () =>
        selectableUsers?.map((user) => ({
          label: user.username,
          value: user.id,
        })) ?? [],
      [selectableUsers],
    );

    const onSubmit: SubmitHandler<EditedExtension> = useStableCallback(
      async (formValues) => {
        const formData = new FormData();
        if (formValues.file !== null) {
          formData.append('file', formValues.file);
        }
        formData.append('name', formValues.name);
        formData.append('description', formValues.description);
        const environmentVariables = formValues.environmentVariables
          .split('\n')
          .reduce(
            (acc, line) => {
              const [key, value] = line.split('=');
              // Fixed noUncheckedIndexedAccess NIM-19408
              acc[key!] = value!;
              return acc;
            },
            {} as Record<string, string>,
          );
        formData.append(
          'environmentVariables',
          JSON.stringify(environmentVariables),
        );
        formData.append('slug', formValues.slug);

        if (formValues.type === null) {
          throw new Error('Type is required');
        }
        formData.append('type', formValues.type);

        if (formValues.accessPermission === null) {
          throw new Error('Access permission is required');
        }
        formData.append('accessPermission', formValues.accessPermission);

        if (formValues.accessPermission === 'selected-users') {
          formData.append(
            'accessibleByUsers',
            JSON.stringify(formValues.accessibleByUsers),
          );
        }
        formData.append(
          'resources',
          JSON.stringify({
            cpu: Number(formValues.cpu),
            memory: Number(formValues.memory),
          }),
        );

        await api.request({
          url:
            formValues.type === 'docker-zip'
              ? 'extensions/docker-zip'
              : 'extensions/docker-image',
          method: 'POST',
          data: formData,
          formData: true,
        });
        notifications.showNotification({
          type: 'success',
          message: 'Extension has been added',
        });
        props.onClose();
        props.onSubmit?.();
      },
    );

    return (
      <FormProvider {...methods}>
        <SidePanel
          title={isReadonly ? 'Extension Details' : 'Add Extension'}
          closeOnBackdropClick={false}
          onClose={props.onClose}
          footer={({ closeModal }) => (
            <div className='flex gap-xs'>
              {!isReadonly && (
                <Button
                  type='submit'
                  disabled={!formState.isDirty}
                  loading={formState.isSubmitting}
                  variant='primary'
                  onClick={handleSubmit(onSubmit)}
                  className='flex-1'
                  icon='fa-save'
                >
                  Save
                </Button>
              )}
              <Button variant='outline' onClick={closeModal} className='flex-1'>
                Close
              </Button>
            </div>
          )}
        >
          <form>
            <Card className='flex flex-col gap p-sm'>
              <div className='text-level-5 font-semibold'>Extension setup</div>
              <FormTextField
                control={control}
                name='name'
                type='text'
                label='Name'
                onClear={
                  formState.dirtyFields.name
                    ? () => {
                        resetField('name');
                      }
                    : undefined
                }
                disabled={isReadonly}
              />
              <FormTextField
                control={control}
                name='slug'
                type='text'
                label='Slug'
                onClear={
                  formState.dirtyFields.name
                    ? () => {
                        resetField('slug');
                      }
                    : undefined
                }
                readOnly={isReadonly}
                disabled={isReadonly}
              />
              <FormSelect.Mandatory
                control={control}
                name='type'
                label='Type'
                options={fileTypeOptions}
                rules={{
                  onChange: () => {
                    methods.clearErrors('file');
                  },
                }}
                disabled={isReadonly}
              />
              <FormTextArea
                control={control}
                name='description'
                label='Description'
                disabled={isReadonly}
              />
              <FormTextArea
                control={control}
                name='environmentVariables'
                label='Environment Variables'
                disabled={isReadonly}
                rules={{
                  validate: (value) => {
                    if (!value) {
                      return true;
                    }
                    return validateEnvironmentVariables({
                      environmentVariables: value,
                      keyValueFormatError:
                        'Invalid key-value format. Please ensure each environment variable is in the format "key=value", with each entry on a separate line.',
                    });
                  },
                }}
              />

              {!isReadonly && (
                <FormFileUpload
                  control={control}
                  name='file'
                  label='Select import file'
                  className='w-full'
                />
              )}
              <div className='flex gap-xs'>
                <FormTextField
                  className='flex-1'
                  control={control}
                  name='cpu'
                  type='number'
                  label='CPU'
                  disabled={isReadonly}
                  endAdornment='units'
                  step={0.01}
                  min={0.01}
                />
                <FormTextField
                  className='flex-1'
                  control={control}
                  name='memory'
                  type='number'
                  label='Memory'
                  disabled={isReadonly}
                  endAdornment='MB'
                />
              </div>

              <FormSelect.Mandatory
                control={control}
                name='accessPermission'
                label='Access Permission'
                options={accessPermissionOptions}
                disabled={isReadonly}
              />
              {accessPermission === 'selected-users' && (
                <FormMultiSelect
                  control={control}
                  name='accessibleByUsers'
                  label='Accessible By Users'
                  options={selectableUsersOptions}
                  disabled={isReadonly}
                />
              )}
              {isReadonly && props.logs && (
                <ExtensionsDialogLogs
                  extensionId={props.initialValues!.id}
                  logs={props.logs}
                  logsRefreshing={props.logsRefreshing ?? false}
                  refreshExtensionLogs={() => props.onRefreshLogs?.()}
                />
              )}
            </Card>
          </form>
        </SidePanel>
      </FormProvider>
    );
  },
);
